'use client';

import { useEffect, useRef, useState } from 'react';

import { useGSAP } from '@gsap/react';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import cx from 'clsx';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';

import ChevronDown from '@/components/SvgIcons/ChevronDown';
import { hasNavLinkChildren, navLinks } from '@/config/navigation.config';
import { Link, usePathname } from '@/i18n/navigation';
import colors from '@/styles/themes/colors';

import styles from './MenuDrawer.module.scss';

interface MenuDrawerProps {
  open: boolean;
  onClose: () => void;
}

const MenuDrawer = ({ open, onClose }: MenuDrawerProps) => {
  const t = useTranslations('common');
  const pathname = usePathname();
  const containerRef = useRef<HTMLDivElement>(null);
  const navLinksRef = useRef<HTMLLIElement[]>([]);
  const footerRef = useRef<HTMLDivElement>(null);
  const [expandedLinks, setExpandedLinks] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setExpandedLinks(new Set());
    }
  }, [open]);

  useGSAP(
    () => {
      const tl = gsap.timeline();

      if (open) {
        tl.to(containerRef.current, {
          opacity: 1,
          visibility: 'visible',
          backdropFilter: 'blur(8px)',
          duration: 0.3,
          ease: 'power2.out',
        })
          .fromTo(
            navLinksRef.current,
            { x: -50, opacity: 0 },
            {
              x: 0,
              opacity: 1,
              duration: 0.25,
              stagger: 0.1,
              ease: 'power2.out',
            },
            '-=0.3'
          )
          .fromTo(
            footerRef.current,
            { y: 50, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.3,
              ease: 'power2.out',
            },
            '-=0.2'
          );
      } else {
        tl.to([navLinksRef.current, footerRef.current], {
          opacity: 0,
          duration: 0.15,
          ease: 'power2.in',
        }).to(containerRef.current, {
          opacity: 0,
          duration: 0.15,
          ease: 'power2.in',
          onComplete: () => {
            gsap.set(containerRef.current, { visibility: 'hidden' });
          },
        });
      }
    },
    { dependencies: [open] }
  );

  const handleExpandClick = (href: string) => {
    setExpandedLinks(prev => {
      const next = new Set(prev);

      if (next.has(href)) {
        next.delete(href);
      } else {
        next.add(href);
      }

      return next;
    });
  };

  return (
    <Box ref={containerRef} className={cx(styles.container, open && styles.open)}>
      <Box className={styles.drawerContent}>
        <Box component="nav" className={styles.nav}>
          <List classes={{ root: styles.listRoot }} className={styles.navList} disablePadding>
            {navLinks.map((link, index) => {
              if (hasNavLinkChildren(link)) {
                const isExpanded = expandedLinks.has(link.href);
                const isActive =
                  pathname === link.href ||
                  pathname.startsWith(`${link.href}/`) ||
                  link.children.some(child => pathname === child.href || pathname.startsWith(`${child.href}/`));

                return (
                  <ListItem
                    key={link.href}
                    disablePadding
                    sx={{ flexDirection: 'column', alignItems: 'stretch' }}
                    ref={el => {
                      if (navLinksRef.current && el) {
                        navLinksRef.current[index] = el;
                      }
                    }}
                  >
                    <Box
                      className={cx(styles.expandableRow, isActive && styles.expandableRowActive)}
                      sx={{ display: 'flex', width: '100%', alignItems: 'stretch' }}
                    >
                      <ListItemButton
                        component={Link}
                        href={link.href as '/'}
                        className={`${styles.navLink} ${isActive ? styles.active : ''}`}
                        onClick={onClose}
                        disableRipple
                        sx={{ flex: 1, justifyContent: 'flex-start' }}
                      >
                        {isActive && <Box component="span" className={styles.activeDot} />}
                        {t(link.labelKey)}
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => handleExpandClick(link.href)}
                        disableRipple
                        className={styles.expandButton}
                        aria-expanded={isExpanded}
                      >
                        <Box
                          component="span"
                          className={styles.chevron}
                          sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                        >
                          <ChevronDown size={24} />
                        </Box>
                      </ListItemButton>
                    </Box>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding className={styles.sublinksList}>
                        {link.children.map(child => {
                          const isChildActive = pathname === child.href || pathname.startsWith(`${child.href}/`);

                          return (
                            <ListItem key={child.href} disablePadding>
                              <ListItemButton
                                component={Link}
                                href={child.href as '/'}
                                className={`${styles.sublink} ${isChildActive ? styles.active : ''}`}
                                onClick={onClose}
                                disableRipple
                              >
                                {isChildActive && <Box component="span" className={styles.activeDot} />}
                                {t(child.labelKey)}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </ListItem>
                );
              }

              const isActive = pathname === link.href || pathname.startsWith(`${link.href}/`);

              return (
                <ListItem
                  key={link.href}
                  disablePadding
                  ref={el => {
                    if (navLinksRef.current && el) {
                      navLinksRef.current[index] = el;
                    }
                  }}
                >
                  <ListItemButton
                    component={Link}
                    href={link.href as '/'}
                    className={`${styles.navLink} ${isActive ? styles.active : ''}`}
                    onClick={onClose}
                    disableRipple
                  >
                    {isActive && <Box component="span" className={styles.activeDot} />}
                    {t(link.labelKey)}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
        <Box ref={footerRef} className={styles.drawerFooter}>
          <Button
            component={Link}
            href="/contact"
            onClick={onClose}
            fullWidth
            sx={{ minHeight: '65px', borderRadius: '24px' }}
          >
            <Typography variant="subtitle1" component="p" color={colors.graytone950}>
              {t('getInTouch')}
            </Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MenuDrawer;
